import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Users, DollarSign, Repeat, Star, Briefcase, CheckCircle, Hammer, Building, Scale, CreditCard, Search, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function ForProvidersPage() {
  const providerTypes = [
    { icon: Hammer, name: 'Contractors & Rehab Crews', desc: 'Renovation specialists who understand investor timelines' },
    { icon: CreditCard, name: 'Hard Money & Private Lenders', desc: 'Financing partners for fix & flip and BRRRR deals' },
    { icon: Scale, name: 'Real Estate Attorneys', desc: 'Legal experts in investment transactions' },
    { icon: Search, name: 'Home Inspectors', desc: 'Thorough inspections for investment properties' },
    { icon: Building, name: 'Property Managers', desc: 'Professional management for rental portfolios' },
    { icon: Shield, name: 'Title & Escrow Companies', desc: 'Fast closings for investment transactions' }
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Reach Active Investors',
      description: 'Get in front of buyers and sellers who are actively doing deals — not someday dreamers. Our members are serious about real estate investing.'
    },
    {
      icon: Repeat,
      title: 'Build Recurring Revenue',
      description: 'Investors do multiple deals per year. One happy client means repeat business for years to come, plus referrals to their network.'
    },
    {
      icon: Star,
      title: 'Showcase Your Expertise',
      description: 'Build credibility with reviews from satisfied clients. Your track record speaks louder than any advertisement.'
    },
    {
      icon: DollarSign,
      title: 'Higher-Value Projects',
      description: 'Investor projects are often larger in scope. More revenue per client with fewer sales conversations.'
    },
    {
      icon: Users,
      title: 'Network Effect',
      description: 'Investors talk to each other. Great service leads to word-of-mouth referrals and a growing client base.'
    },
    {
      icon: Briefcase,
      title: 'Streamlined Lead Flow',
      description: 'Investors contact you directly through the platform. No lead aggregators or bidding wars.'
    }
  ];

  const whyInvestors = [
    'They understand scope and don\'t nickel-and-dime',
    'They have realistic budgets and timelines',
    'They pay on time because time is money',
    'They become repeat customers for years',
    'They refer other investors in their network'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/8961065/pexels-photo-8961065.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Service providers at work"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/60"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-[#5DADE2]/20 border border-[#5DADE2]/30 text-[#5DADE2] text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Briefcase className="h-4 w-4" />
              For Service Providers
            </span>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-6" data-testid="provider-hero-title">
              Get in Front of Active Real Estate Investors
            </h1>
            
            <p className="text-xl text-slate-300 leading-relaxed mb-8 max-w-xl">
              Stop chasing cold leads. NextFlip puts you directly in front of investors who need your services right now — contractors, lenders, attorneys, inspectors, and more.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button asChild size="lg" className="bg-[#5DADE2] hover:bg-[#4A9BD1] text-white rounded-full px-8 text-lg h-14" data-testid="provider-signup-btn">
                <Link to="/signup">
                  List Your Business
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full px-8 text-lg h-14">
                <Link to="/directory">
                  View Directory
                </Link>
              </Button>
            </div>

            <p className="text-slate-400 text-sm">
              Free to create a profile. Paid premium listings available for more visibility.
            </p>
          </div>
        </div>
      </section>

      {/* Provider Types */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Service Providers Investors Are Looking For
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Whatever your specialty, investors need your expertise to execute their deals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {providerTypes.map((type, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 flex items-start gap-4 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <type.icon className="h-6 w-6 text-[#5DADE2]" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">{type.name}</h3>
                  <p className="text-sm text-slate-600">{type.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Investors Are Better Clients */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://images.pexels.com/photos/7031607/pexels-photo-7031607.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Business meeting"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
                Why Real Estate Investors Are Your Best Clients
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Forget difficult homeowners and low-budget jobs. Investors treat service providers as partners, not vendors.
              </p>
              <div className="space-y-4">
                {whyInvestors.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle className="h-6 w-6 text-[#5DADE2] flex-shrink-0" />
                    <span className="text-lg text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Grow Your Business with NextFlip
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              More than just a directory — it's a platform designed to help you win more business.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <div key={i} className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700 hover:border-[#5DADE2]/50 transition-all group">
                <div className="w-14 h-14 bg-gradient-to-br from-[#5DADE2] to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <benefit.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-400 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profile Features */}
      <section className="py-24 bg-gradient-to-br from-[#5DADE2] to-blue-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
                Your Profile Works for You 24/7
              </h2>
              <p className="text-lg text-white/90 mb-8">
                Create a compelling profile that showcases your expertise and attracts the right clients.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Business description & services',
                  'Service area & markets',
                  'Photo gallery & portfolio',
                  'Client reviews & ratings',
                  'Contact information',
                  'Response time indicator',
                  'Verified badge option',
                  'Featured listing placement'
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-sky-200" />
                    <span className="text-white">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-sky-100 rounded-xl flex items-center justify-center">
                  <Hammer className="h-8 w-8 text-[#5DADE2]" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">Premier Renovations</h3>
                  <p className="text-slate-500">General Contractor • Atlanta, GA</p>
                </div>
              </div>
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="h-5 w-5 fill-[#5DADE2] text-[#5DADE2]" />
                ))}
                <span className="text-slate-600 ml-2">5.0 (47 reviews)</span>
              </div>
              <p className="text-slate-600 mb-4">
                "Investor-focused renovation company specializing in fast turnarounds for fix & flip projects. 10+ years experience..."
              </p>
              <div className="flex gap-2">
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Fix & Flip</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">BRRRR</span>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Multi-Family</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Start Getting Leads Today
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Create Your Profile', desc: 'Sign up free and build your provider profile. Add your services, service areas, and what makes you different.' },
              { step: '02', title: 'Get Found by Investors', desc: 'Investors searching for your services will find you. Our directory highlights your ratings and expertise.' },
              { step: '03', title: 'Win More Business', desc: 'Receive direct inquiries from qualified leads. Build relationships that turn into repeat business.' }
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-[#5DADE2]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-[#5DADE2]">{item.step}</span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#5DADE2] to-blue-600">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Put Your Business in Front of Serious Investors
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join the directory trusted by thousands of real estate investors. Create your free profile and start receiving qualified leads today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-[#5DADE2] hover:bg-slate-100 rounded-full px-8 text-lg h-14 font-semibold">
              <Link to="/signup">
                Create Free Profile
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 text-lg h-14">
              <Link to="/directory">
                Browse the Directory
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
